/**
 * @brisa/shared/logger — logger canônico + PII scrubber
 *
 * Wrapper sobre pino com scrubber aplicado em cada chamada.
 * ESLint rule `no-console` sugere este módulo.
 */
import { scrubPII } from './scrubber';

export { scrubPII } from './scrubber';

export interface Logger {
  debug(message: string, context?: Record<string, unknown>): void;
  info(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string | Error, context?: Record<string, unknown>): void;
  child(scope: Record<string, unknown>): Logger;
}

function safeConsole(
  level: 'debug' | 'info' | 'warn' | 'error',
  message: string,
  context?: Record<string, unknown>,
): void {
  if (typeof process === 'undefined' || process.env?.NODE_ENV === 'test') return;
  const scrubbedCtx = context ? scrubPII(context) : undefined;
  // eslint-disable-next-line no-console
  const fn = console[level] ?? console.log;
  fn(JSON.stringify({ level, message, ...(scrubbedCtx as object | undefined), ts: Date.now() }));
}

function makeLogger(scope: Record<string, unknown> = {}): Logger {
  const mergedScope = scope;
  return {
    debug(message, context) {
      safeConsole('debug', message, { ...mergedScope, ...context });
    },
    info(message, context) {
      safeConsole('info', message, { ...mergedScope, ...context });
    },
    warn(message, context) {
      safeConsole('warn', message, { ...mergedScope, ...context });
    },
    error(message, context) {
      const msg = message instanceof Error ? message.message : message;
      const ctx = message instanceof Error ? { ...context, stack: message.stack } : context;
      safeConsole('error', msg, { ...mergedScope, ...ctx });
    },
    child(childScope) {
      return makeLogger({ ...mergedScope, ...childScope });
    },
  };
}

export const logger: Logger = makeLogger();
